'use client';

import { useCallback, useEffect, useReducer } from 'react';
import { Dictionary } from '@ts/dictionary';
import VariantSelector from './variantSelector';
import reducer from './reducer';
import initiateState from './utils/initiateState';
import match from 'lodash.ismatch';
import { VariantAttribute } from '@ts/variant';
import { useRouter } from 'next/navigation';
import { optionSorting, optionSortingAlwaysCheapestFirst } from './utils/utils';
import type { Variant as VariantType } from '@ts/variant';
import { sendVoyadoProductViewEvent } from '@utils/voyadoTracking';
import { isInStock } from '@utils/stockStatus/isInStock';

type Props = {
  variants: VariantType[];
  variant?: VariantType;
  dictionary: Dictionary;
  attributes: { key: string; Label: string; Values: string[] }[];
  className?: string;
  breadcrumbLabels?: (string | null | undefined)[];
};

export default function Variant(props: Props) {
  const { attributes, dictionary, variant, breadcrumbLabels } = props;
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, props, (s) => initiateState(s));

  useEffect(() => {
    if (variant && breadcrumbLabels) {
      const labels: string[] = breadcrumbLabels.filter((b) => b) as string[];
      sendVoyadoProductViewEvent({
        breadcrumbLabels: labels,
        itemId: variant.id,
      });
    }
  }, [variant, breadcrumbLabels]);

  const variantParamName: string = 'variant';

  const onChange = useCallback(
    (
      name: string,
      value: string | number,
      variantAttributes: Map<string, VariantAttribute[]>,
    ) => {
      const convertedValue = isNaN(+value) ? value : +value;

      const selection = { ...state.selection, [name]: convertedValue };
      // Match variants based on the current selection
      const variants = state.variants.filter((variant) =>
        match(variant, selection),
      );

      // Sort matching variants by stock status
      const sortedVariantsByStockStatus = variants.sort((a, b) => {
        return isInStock(a.stockStatus) ? -1 : isInStock(b.stockStatus) ? 1 : 0;
      });

      // Create a Set to store unique norce_variants
      const uniqueVariants = new Set();

      // Filter out duplicate variants with the same norce_variant
      const filteredVariants = sortedVariantsByStockStatus.filter((variant) => {
        if (!uniqueVariants.has(variant.norce_variant)) {
          uniqueVariants.add(variant.norce_variant);
          return true;
        }

        return false;
      });

      if (filteredVariants.length === 1) {
        const [variant] = filteredVariants;

        const newSearchParams = new URLSearchParams(
          window.location.search.toString(),
        );

        newSearchParams.set(variantParamName, variant.id);

        router.replace(
          `${window.location.pathname}?${newSearchParams.toString()}`,
          {
            scroll: false,
          },
        );
      }

      dispatch({
        type: 'SET_ATTRIBUTE',
        payload: { [name]: convertedValue },
        prevOptions: { name, variantAttributes },
      });
    },
    [router, state.selection, state.variants],
  );

  useEffect(() => {
    if (props.variant) {
      dispatch({ type: 'SET_VARIANT', payload: props.variant });
      const newSearchParams = new URLSearchParams(
        window.location.search.toString(),
      );

      const variantParam = newSearchParams.get(variantParamName);

      if (variantParam || !props.variant) {
        return;
      }

      newSearchParams.set(variantParamName, props.variant.id);

      router.replace(
        `${window.location.pathname}?${newSearchParams.toString()}`,
        {
          scroll: false,
        },
      );
    }
  }, [router, props.variant]);

  const optionSortingToUse =
    attributes.length > 1 ? optionSortingAlwaysCheapestFirst : optionSorting;

  return (
    <div className="mb-2 md:mb-6" data-testid="ProductChoiceSelector">
      {attributes.map(({ key, Label }) => {
        const attribute = state.options.get(key);

        if (!attribute) return null;

        const options = [...attribute].sort(optionSortingToUse);

        return (
          <VariantSelector
            attribute={attribute}
            dictionary={dictionary}
            key={key}
            name={key}
            label={Label}
            value={state.selection[key] ?? null}
            disabled={![...attribute].length}
            options={options}
            onChange={(value) => onChange(key, value, attribute)}
          />
        );
      })}
    </div>
  );
}
